/** FELVORA — Inmobiliaria F6 P2 · Cobranza de rentas: bandeja de morosos + bitácora. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, MessageSquarePlus, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const mxn = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

export default function CobranzaRentas() {
  const [morosos, setMorosos] = useState([]);
  const [gestionOpen, setGestionOpen] = useState(null);
  const [gestion, setGestion] = useState({ texto: "", canal: "whatsapp" });

  const cargar = () => {
    api.get("/inmobiliaria/arrendamientos/morosos").then((r) => setMorosos(r.data)).catch(() => {});
  };
  useEffect(() => { cargar(); }, []);

  const guardarGestion = async (contratoId) => {
    try {
      await api.post(`/inmobiliaria/arrendamientos/${contratoId}/gestion`, gestion);
      toast.success("Gestión registrada");
      setGestionOpen(null); setGestion({ texto: "", canal: "whatsapp" });
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="cobranza-rentas-page">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">
            <AlertTriangle className="mr-2 inline h-6 w-6 text-red-700" />
            Cobranza de rentas
          </h1>
          <p className="text-sm text-slate-500">Bandeja de morosos autoalimentada · bitácora por contrato.</p>
        </div>
        <Button variant="outline" onClick={cargar} data-testid="btn-refresh-morosos">
          <RefreshCw className="mr-1 h-4 w-4" /> Actualizar
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="divide-y">
            {morosos.length === 0 && <p className="p-6 text-center text-sm text-slate-500">Sin morosos vigentes.</p>}
            {morosos.map((m) => (
              <div key={m.contrato_id} className="p-3" data-testid={`moroso-${m.contrato_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-semibold text-slate-900">
                      Contrato {m.contrato_id.slice(0, 8)}… · inmueble {m.inmueble_id.slice(0, 8)}…
                    </p>
                    <p className="text-xs text-slate-500">
                      Renta {mxn(m.renta_mensual_sin_iva)} · atraso máx <strong>{m.dias_max}d</strong>
                      {" "}· cuotas vencidas {m.cuotas_vencidas.length}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className="bg-red-100 text-red-900">Vencido {mxn(m.monto_vencido)}</Badge>
                    {m.pena_fija_aplicada > 0 && (
                      <Badge className="bg-amber-100 text-amber-900">Pena fija {mxn(m.pena_fija_aplicada)}</Badge>
                    )}
                    <Button size="sm" variant="outline" data-testid={`btn-gestion-${m.contrato_id}`}
                            onClick={() => setGestionOpen(m.contrato_id)}>
                      <MessageSquarePlus className="mr-1 h-4 w-4" /> Registrar gestión
                    </Button>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {m.cuotas_vencidas.map((v) => (
                    <Badge key={v.no} variant="outline">
                      C{v.no} · {v.fecha_pago} · {v.dias_atraso}d · {mxn(v.pendiente)}
                    </Badge>
                  ))}
                </div>
                {m.bitacora?.length > 0 && (
                  <div className="mt-2 text-xs text-slate-600">
                    <p className="font-semibold">Últimas gestiones:</p>
                    <ul className="ml-4 list-disc">
                      {m.bitacora.map((g) => (
                        <li key={g.id}>
                          <span className="capitalize">{g.canal}</span> · {g.usuario} · {g.texto}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!gestionOpen} onOpenChange={(v) => !v && setGestionOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Registrar gestión de cobranza</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <label className="text-sm">Canal</label>
            <Select value={gestion.canal} onValueChange={(v) => setGestion({...gestion, canal: v})}>
              <SelectTrigger data-testid="select-canal-gestion"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="llamada">Llamada</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="visita">Visita</SelectItem>
                <SelectItem value="otro">Otro</SelectItem>
              </SelectContent>
            </Select>
            <label className="text-sm">Descripción</label>
            <Input data-testid="input-texto-gestion" value={gestion.texto}
                    onChange={(e) => setGestion({...gestion, texto: e.target.value})} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGestionOpen(null)}>Cancelar</Button>
            <Button data-testid="btn-guardar-gestion" onClick={() => guardarGestion(gestionOpen)}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
